<script setup lang="ts">
import { ref, onMounted, watch } from 'vue';
import { api } from '@/api';
import type { LogResponse, TenantResponse } from '@klever-webhook/shared';

const logs = ref<LogResponse[]>([]);
const configs = ref<TenantResponse[]>([]);
const loading = ref(true);
const error = ref('');
const total = ref(0);

// Filters
const filterTenantId = ref('');
const filterStatus = ref<'' | 'pending' | 'success' | 'failed' | 'skipped'>('');
const limit = ref(20);
const offset = ref(0);

// Preview modal
const previewLogId = ref<string | null>(null);
const previewData = ref<Record<string, unknown> | null>(null);
const previewLoading = ref(false);
const previewError = ref('');

async function loadLogs() {
  loading.value = true;
  try {
    const result = await api.getLogs({
      tenantId: filterTenantId.value || undefined,
      status: filterStatus.value || undefined,
      limit: limit.value,
      offset: offset.value,
    });
    logs.value = result.logs;
    total.value = result.total;
  } catch (e) {
    error.value = 'Impossible de charger les logs';
  } finally {
    loading.value = false;
  }
}

async function loadConfigs() {
  try {
    configs.value = await api.getConfigs();
  } catch (e) {
    // Ignore - just for filter dropdown
  }
}

async function retryLog(logId: string) {
  try {
    await api.retryLog(logId);
    await loadLogs();
  } catch (e) {
    error.value = 'Impossible de relancer le webhook';
  }
}

async function showPreview(logId: string) {
  previewLogId.value = logId;
  previewLoading.value = true;
  previewError.value = '';
  previewData.value = null;
  try {
    const result = await api.previewLog(logId);
    previewData.value = result.metadata;
  } catch (e) {
    previewError.value = 'Impossible de charger la preview';
  } finally {
    previewLoading.value = false;
  }
}

function closePreview() {
  previewLogId.value = null;
  previewData.value = null;
  previewError.value = '';
}

function formatDate(dateStr: string) {
  return new Date(dateStr).toLocaleString();
}

function nextPage() {
  offset.value += limit.value;
  loadLogs();
}

function prevPage() {
  offset.value = Math.max(0, offset.value - limit.value);
  loadLogs();
}

watch([filterTenantId, filterStatus], () => {
  offset.value = 0;
  loadLogs();
});

onMounted(() => {
  loadConfigs();
  loadLogs();
});
</script>

<template>
  <div class="mx-auto py-6 px-4 sm:px-6 lg:px-8">
    <div class="px-4 py-6 sm:px-0">
      <h1 class="text-2xl font-semibold text-gray-900 mb-6">Journaux des webhooks</h1>

      <!-- Filters -->
      <div class="bg-white shadow rounded-lg p-4 mb-6">
        <div class="flex flex-wrap gap-4">
          <div>
            <label class="block text-sm font-medium text-gray-700">Locataire</label>
            <select
              v-model="filterTenantId"
              class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm border px-3 py-2"
            >
              <option value="">Tous les locataires</option>
              <option v-for="config in configs" :key="config.id" :value="config.id">
                {{ config.name }}
              </option>
            </select>
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700">Statut</label>
            <select
              v-model="filterStatus"
              class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm border px-3 py-2"
            >
              <option value="">Tous les statuts</option>
              <option value="pending">En attente</option>
              <option value="success">Succès</option>
              <option value="failed">Échec</option>
              <option value="skipped">Ignoré</option>
            </select>
          </div>
          <div class="flex items-end">
            <button
              @click="loadLogs"
              class="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Actualiser
            </button>
          </div>
        </div>
      </div>

      <div v-if="error" class="mb-4 text-red-600">{{ error }}</div>

      <!-- Logs Table -->
      <div class="bg-white shadow overflow-x-auto sm:rounded-lg">
        <div v-if="loading" class="p-4 text-gray-500">Chargement...</div>
        <div v-else-if="logs.length === 0" class="p-4 text-gray-500">Aucun log trouvé.</div>
        <table v-else class="min-w-full divide-y divide-gray-200">
          <thead class="bg-gray-50">
            <tr>
              <th class="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
              <th class="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Locataire</th>
              <th class="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type d'événement</th>
              <th class="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Document</th>
              <th class="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">N° Document</th>
              <th class="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Falco ID</th>
              <th class="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Statut</th>
              <th class="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Durée</th>
              <th class="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody class="bg-white divide-y divide-gray-200">
            <tr v-for="log in logs" :key="log.id">
              <td class="px-3 py-4 whitespace-nowrap text-sm text-gray-500">{{ formatDate(log.createdAt) }}</td>
              <td class="px-3 py-4 whitespace-nowrap text-sm text-gray-900">{{ log.tenantName }}</td>
              <td class="px-3 py-4 whitespace-nowrap text-sm text-gray-500">
                <code class="text-xs bg-gray-100 px-2 py-1 rounded">{{ log.eventType }}</code>
              </td>
              <td class="px-3 py-4 whitespace-nowrap text-sm text-gray-500">{{ log.documentId || '-' }}</td>
              <td class="px-3 py-4 whitespace-nowrap text-sm text-gray-500">{{ log.documentNumber || '-' }}</td>
              <td class="px-3 py-4 whitespace-nowrap text-sm text-gray-500">{{ log.falcoDocumentId || '-' }}</td>
              <td class="px-3 py-4 whitespace-nowrap">
                <span
                  class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full"
                  :class="{
                    'bg-green-100 text-green-800': log.status === 'success',
                    'bg-yellow-100 text-yellow-800': log.status === 'pending',
                    'bg-red-100 text-red-800': log.status === 'failed',
                    'bg-orange-100 text-orange-800': log.status === 'skipped'
                  }"
                >
                  {{ log.status }}
                </span>
              </td>
              <td class="px-3 py-4 whitespace-nowrap text-sm text-gray-500">
                {{ log.processingTimeMs ? `${log.processingTimeMs}ms` : '-' }}
              </td>
              <td class="px-3 py-4 whitespace-nowrap text-right text-sm font-medium">
                <button
                  @click="showPreview(log.id)"
                  class="text-blue-600 hover:text-blue-900 mr-2"
                >
                  Preview
                </button>
                <button
                  v-if="log.status === 'failed' || log.status === 'skipped'"
                  @click="retryLog(log.id)"
                  class="text-indigo-600 hover:text-indigo-900"
                >
                  Relancer
                </button>
                <span v-if="log.errorMessage" class="ml-2 text-red-500" :title="log.errorMessage">⚠️</span>
              </td>
            </tr>
          </tbody>
        </table>

        <!-- Pagination -->
        <div class="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
          <div class="flex-1 flex justify-between sm:hidden">
            <button
              @click="prevPage"
              :disabled="offset === 0"
              class="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
            >
              Précédent
            </button>
            <button
              @click="nextPage"
              :disabled="offset + limit >= total"
              class="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
            >
              Suivant
            </button>
          </div>
          <div class="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
            <div>
              <p class="text-sm text-gray-700">
                Affichage de
                <span class="font-medium">{{ offset + 1 }}</span>
                à
                <span class="font-medium">{{ Math.min(offset + limit, total) }}</span>
                sur
                <span class="font-medium">{{ total }}</span>
                résultats
              </p>
            </div>
            <div>
              <button
                @click="prevPage"
                :disabled="offset === 0"
                class="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 mr-2"
              >
                Précédent
              </button>
              <button
                @click="nextPage"
                :disabled="offset + limit >= total"
                class="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                Suivant
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Preview Modal -->
  <div v-if="previewLogId" class="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" @click.self="closePreview">
    <div class="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col">
      <div class="flex justify-between items-center p-4 border-b">
        <h2 class="text-lg font-semibold text-gray-900">Falco JSON Preview</h2>
        <button @click="closePreview" class="text-gray-400 hover:text-gray-600 text-2xl leading-none">&times;</button>
      </div>
      <div class="p-4 overflow-auto flex-1">
        <div v-if="previewLoading" class="text-gray-500">Chargement...</div>
        <div v-else-if="previewError" class="text-red-600">{{ previewError }}</div>
        <pre v-else class="bg-gray-50 p-4 rounded text-sm overflow-auto whitespace-pre-wrap font-mono text-gray-800">{{ JSON.stringify(previewData, null, 2) }}</pre>
      </div>
    </div>
  </div>
</template>
